/**
 * `RightSidebar` — shell-level, persistent, retractable right pane.
 * The lawyer's cross-mode monitor surface for missions + standalone
 * harness tasks ("Team of Agents" pane).
 *
 * Two layout modes (asymmetric with the left `Sidebar` — see operator
 * directive 2026-06-22):
 *   - **Desktop expanded** (256px): full pane with header + body.
 *   - **Desktop collapsed**: pane is COMPLETELY HIDDEN — there is no
 *     rail, no truncated window, no in-flow flex sibling. A single
 *     floating `right-sidebar-toggle` button sits alone in the top-
 *     right corner (`Activity` icon — the universal "live monitoring"
 *     glyph; chosen over `PanelRightOpen` because the affordance
 *     advertises what's BEHIND the panel, not just "a panel") and is
 *     the only affordance to bring the pane back. This intentionally
 *     diverges from the left `Sidebar`'s "icon-only rail when
 *     collapsed" pattern.
 *   - **Mobile/tablet (<1024px)**: drawer pattern. Closed = the same
 *     floating top-right button (`right-sidebar-mobile-toggle`
 *     testid); open = full pane slides in from the right as an
 *     `<aside>` with a backdrop. Drawer state uses the SEPARATE
 *     per-session `rightDrawerOpen` slice (NOT persisted) — distinct
 *     from the desktop preference `rightSidebarExpanded`. Avoids two
 *     confusions: a desktop expansion would otherwise pop the drawer
 *     over the route on reload, and a mobile tap would otherwise
 *     stamp `lastUserToggleAt` and disable the §I.2 desktop
 *     auto-expand.
 *
 * Default state on first mount: collapsed (§I.2). The §I.2 smart
 * auto-expand effect inside `TeamOfAgentsPane` flips this to expanded
 * on the first poll if active/awaiting tasks exist AND the lawyer
 * hasn't manually toggled yet (`lastUserToggleAt === null` in the
 * store).
 *
 * Auto-expand caveat under the hidden-when-collapsed model: the pane
 * unmounts when collapsed, so the auto-expand effect can only fire on
 * a poll that runs while the pane is already mounted (a prior session
 * left it expanded, or a deliberate manual expansion). For a freshly-
 * signed-in user with sessionStorage missions data already in cache,
 * the auto-expand still fires on the first render after the pane
 * mounts. Cold-start auto-expand (pane never mounted) requires a
 * background poller in a future revision — flagged as a follow-up.
 *
 * Plan: ai-docs/lexide-right-sidebar-implementation-plan.md (§C/§F)
 */
import { Activity, PanelRightClose, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import {
  RIGHT_SIDEBAR_DEFAULT_PX,
  RIGHT_SIDEBAR_MAX_PX,
  RIGHT_SIDEBAR_MIN_PX,
  useUIStore,
} from "@/stores/useUIStore";
import { isCompactViewport, useViewportSize } from "@/hooks/useViewportSize";
import { isRightSidebarOnScreen } from "@/lib/right-sidebar-visibility";
import { TeamOfAgentsPane } from "@/components/team-of-agents/TeamOfAgentsPane";
import { PanelErrorBoundary } from "@/components/errors/PanelErrorBoundary";
import { ResizeHandle } from "@/components/shell/ResizeHandle";

/** Default expanded width — the historical 256px. Kept exported for any
 *  existing consumer importing it; live width now comes from
 *  `useUIStore.rightSidebarWidth` (operator-resizable 2026-06-24). */
export const RIGHT_SIDEBAR_EXPANDED_PX = RIGHT_SIDEBAR_DEFAULT_PX;

export function RightSidebar() {
  const { t } = useTranslation();
  // Desktop layout preference (persisted; drives the 256/48px width).
  const expanded = useUIStore((s) => s.rightSidebarExpanded);
  const toggle = useUIStore((s) => s.toggleRightSidebar);
  // Mobile drawer open state (per-session; distinct from desktop —
  // see useUIStore docstring + review/wf07tgf9h findings M1/M2).
  const drawerOpen = useUIStore((s) => s.rightDrawerOpen);
  const setDrawerOpen = useUIStore((s) => s.setRightDrawerOpen);
  // Desktop width (persisted, clamped [100, 640]). Operator directive
  // 2026-06-24 — the pane should be resizable down to 100px.
  const width = useUIStore((s) => s.rightSidebarWidth);
  const setWidth = useUIStore((s) => s.setRightSidebarWidth);

  const viewport = useViewportSize();
  const isCompact = isCompactViewport(viewport);
  // The SAME predicate the infobar's mission ticker suppresses on, so the two
  // can never disagree about whether this pane is on screen (I-4). Asserted
  // here rather than merely documented: the branches below are its definition,
  // and this keeps them honest if either side is edited alone.
  const onScreen = isRightSidebarOnScreen(viewport, expanded, drawerOpen);

  // Mobile/tablet: drawer pattern, driven by the dedicated
  // (non-persisted) drawer slice. NEVER calls
  // `setRightSidebarExpanded` from here — that would stamp
  // `lastUserToggleAt` and permanently disable the §I.2 desktop
  // auto-expand.
  if (isCompact) {
    if (!onScreen) {
      return <FloatingExpandButton testid="right-sidebar-mobile-toggle" onClick={() => setDrawerOpen(true)} />;
    }
    return (
      <>
        <div
          data-testid="right-sidebar-backdrop"
          aria-hidden="true"
          onClick={() => setDrawerOpen(false)}
          className="fixed inset-0 z-30 bg-black/40 dark:bg-black/60"
        />
        <aside
          data-testid="right-sidebar"
          data-layout="drawer"
          aria-label={t("rightSidebar.ariaLabel")}
          aria-expanded={true}
          className={cn(
            "fixed right-0 top-0 z-40 flex h-full w-72 max-w-[85vw] flex-col border-l border-cream-200/60 bg-white shadow-xl dark:border-coffee-700/60 dark:bg-coffee-950",
            "overflow-hidden",
          )}
        >
          <RightSidebarHeader expanded={true} onToggle={() => setDrawerOpen(false)} isCompact={true} />
          <RightSidebarBody />
        </aside>
      </>
    );
  }

  // Desktop COLLAPSED: pane is COMPLETELY HIDDEN. The expand toggle
  // for the Agents pane lives in the left sidebar's `SidebarUtility`
  // (the `AgentsRow` chip, alongside Search / Gateway / Theme / User)
  // — operator directive 2026-06-26: consolidate every shell
  // affordance into one icon collection, dropping the standalone
  // right rail / floating overlay that kept colliding with case-
  // header content.
  if (!onScreen) {
    return null;
  }

  // Desktop EXPANDED: in-flow flex sibling of ActivityBar + SidePanel.
  return (
    <aside
      data-testid="right-sidebar"
      data-layout="expanded"
      aria-label={t("rightSidebar.ariaLabel")}
      aria-expanded={true}
      // `position: relative` so the absolute-positioned <ResizeHandle/>
      // anchors to the left edge. Width comes from the persisted store
      // slice — clamps to [RIGHT_SIDEBAR_MIN_PX, RIGHT_SIDEBAR_MAX_PX]
      // inside the setter so a drag can never escape the bounds.
      style={{ width }}
      className={cn(
        "relative flex shrink-0 flex-col overflow-hidden border-l border-cream-200/60 bg-white dark:border-coffee-700/60 dark:bg-coffee-950",
      )}
    >
      <ResizeHandle
        side="left"
        ariaLabel={t("rightSidebar.resizeAriaLabel")}
        ariaValueNow={width}
        ariaValueMin={RIGHT_SIDEBAR_MIN_PX}
        ariaValueMax={RIGHT_SIDEBAR_MAX_PX}
        testid="right-sidebar-resize-handle"
        // getState() reads the LIVE width on every move so successive
        // pointermove deltas accumulate against the running value, not
        // the closure captured at drag-start.
        onResize={(dx) =>
          setWidth(useUIStore.getState().rightSidebarWidth + dx)
        }
      />
      <RightSidebarHeader expanded={true} onToggle={toggle} isCompact={false} />
      <RightSidebarBody />
    </aside>
  );
}

/** Floating top-right expand affordance. Shared shape across the
 *  desktop-collapsed and mobile-drawer-closed states — both render
 *  this button and nothing else, leaving the canvas otherwise empty.
 *  testid differs so the e2e specs can target each mode unambiguously
 *  (desktop spec uses `right-sidebar-toggle`, mobile spec uses
 *  `right-sidebar-mobile-toggle`). */
function FloatingExpandButton(props: {
  testid: "right-sidebar-toggle" | "right-sidebar-mobile-toggle";
  onClick: () => void;
}) {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      data-testid={props.testid}
      aria-label={t("rightSidebar.expandAriaLabel")}
      aria-expanded={false}
      title={t("rightSidebar.expandAriaLabel")}
      onClick={props.onClick}
      className="fixed right-3 top-3 z-30 flex h-10 w-10 items-center justify-center rounded-full border border-cream-200/60 bg-white text-slate-700 shadow-md hover:bg-cream-100 dark:border-coffee-700/60 dark:bg-coffee-900 dark:text-slate-200 dark:hover:bg-coffee-800"
    >
      <Activity size={18} />
    </button>
  );
}

function RightSidebarHeader(props: {
  expanded: boolean;
  onToggle: () => void;
  isCompact: boolean;
}) {
  const { t } = useTranslation();
  const { expanded, onToggle, isCompact } = props;

  // Mobile/drawer: header has the close X. Desktop: header has the
  // chevron toggle. In both cases the title shows when expanded.
  return (
    <div
      className={cn(
        "flex items-center border-b border-cream-200/60 dark:border-coffee-700/60",
        expanded ? "h-12 px-3" : "h-12 justify-center",
      )}
    >
      {expanded && (
        <h2 className="flex-1 truncate text-sm font-medium text-slate-700 dark:text-slate-200">
          {t("rightSidebar.title")}
        </h2>
      )}
      <button
        type="button"
        data-testid="right-sidebar-toggle"
        aria-label={
          expanded
            ? t("rightSidebar.collapseAriaLabel")
            : t("rightSidebar.expandAriaLabel")
        }
        aria-expanded={expanded}
        title={expanded ? t("rightSidebar.collapseAriaLabel") : t("rightSidebar.expandAriaLabel")}
        onClick={onToggle}
        className="flex h-8 w-8 items-center justify-center rounded-md text-slate-500 hover:bg-cream-100 hover:text-slate-700 dark:text-slate-400 dark:hover:bg-coffee-800 dark:hover:text-slate-200"
      >
        {isCompact ? <X size={16} /> : <PanelRightClose size={16} />}
      </button>
    </div>
  );
}

function RightSidebarBody() {
  // S1: TeamOfAgentsPane is the first (and v1 only) monitor slot.
  // Future v2 slots (cron runs, channel pairings) would render here
  // alongside it via the slot system documented in plan §C.2.
  //
  // ⚠ WRAPPED 2026-08-01, and the wrapping is the more important half of that
  // day's fix. A missing optional field in one mission record threw inside this
  // pane, and with nothing to catch it the ROUTE-level boundary did — blanking
  // the ENTIRE case view, unrecoverable by "Try again" or by reloading, so the
  // lawyer lost the workspace to a defect in a read-only monitor. The specific
  // throw is fixed (`missionNodes`), but the blast radius was the real defect:
  // a MONITOR must never be able to take down the thing it monitors. Same
  // containment `<PanelErrorBoundary label="Chat">` already gives the chat pane.
  return (
    <div data-testid="right-sidebar-body" className="flex flex-1 flex-col overflow-hidden">
      <PanelErrorBoundary label="Team of Agents">
        <TeamOfAgentsPane />
      </PanelErrorBoundary>
    </div>
  );
}
